import { useEffect, useState, ReactNode } from "react";
import { useLocation } from "wouter";
import { Sidebar } from "./Sidebar";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsDocked } from "@/hooks/use-mobile";

interface AppLayoutProps {
  children: ReactNode;
  sidebar?: boolean;
}

/**
 * Shell for every signed-in page: the library rail plus the page content.
 *
 * The rail is docked as a flex sibling on wide screens and an overlay drawer
 * below that. It used to be an overlay at every width *and* remember that it
 * was open, so returning to the app put a modal backdrop over the dashboard
 * you had come back for. Open state is deliberately not persisted now — when
 * the rail is docked there is nothing to persist, and when it is a drawer the
 * right default is closed.
 */
export function AppLayout({ children, sidebar = true }: AppLayoutProps) {
  const docked = useIsDocked();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [location] = useLocation();

  // Navigating from inside the drawer should dismiss it; the docked rail stays.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location]);

  return (
    <div className="flex min-h-screen bg-background">
      {sidebar && (
        <Sidebar
          docked={docked}
          isOpen={docked || drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {sidebar && !docked && (
          <header className="desktop-chrome-offset sticky top-0 z-30 flex h-12 shrink-0 items-center border-b border-border bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open library"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </header>
        )}

        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
